import type { RuntimeDiagnosticsProcess } from "@cmmd-forge/contracts";

import type { StatusTone } from "./DiagnosticsSettings.types";

export interface ProcessTableRow {
  readonly process: RuntimeDiagnosticsProcess;
  readonly depth: number;
  readonly isLast: boolean;
  readonly ancestorIsLast: readonly boolean[];
}

function buildProcessTreeRows(
  all: ReadonlyArray<RuntimeDiagnosticsProcess>,
  rootPid: number,
): ReadonlyArray<ProcessTableRow> {
  const childrenByParent = new Map<number, RuntimeDiagnosticsProcess[]>();
  for (const process of all) {
    const siblings = childrenByParent.get(process.ppid) ?? [];
    siblings.push(process);
    childrenByParent.set(process.ppid, siblings);
  }
  const rows: ProcessTableRow[] = [];
  const visited = new Set<number>();
  const visit = (
    pid: number,
    depth: number,
    isLast: boolean,
    ancestorIsLast: readonly boolean[],
  ): void => {
    if (visited.has(pid)) return;
    visited.add(pid);
    const process = all.find((candidate) => candidate.pid === pid);
    if (!process) return;
    rows.push({ process, depth, isLast, ancestorIsLast });
    const children = childrenByParent.get(pid) ?? [];
    children.forEach((child, index) => {
      visit(child.pid, depth + 1, index === children.length - 1, [...ancestorIsLast, isLast]);
    });
  };
  visit(rootPid, 0, true, []);
  return rows;
}

export function buildProcessTableRows({
  processes,
  rootPid,
}: {
  readonly processes: ReadonlyArray<RuntimeDiagnosticsProcess>;
  readonly rootPid: number | null;
}): ReadonlyArray<ProcessTableRow> {
  const treeRows = rootPid !== null ? buildProcessTreeRows(processes, rootPid) : [];
  if (treeRows.length > 0) return treeRows;
  return processes.map((process, index) => ({
    process,
    depth: 0,
    isLast: index === processes.length - 1,
    ancestorIsLast: [],
  }));
}

export function processCommandLabel(process: RuntimeDiagnosticsProcess): string {
  const command = process.command.trim();
  if (!command) return "unknown";
  const [first] = command.split(/\s+/);
  return first?.split("/").at(-1) ?? command;
}

export function processTreePrefix(row: ProcessTableRow): string {
  if (row.depth === 0) return "";
  const trunk = row.ancestorIsLast
    .slice(1)
    .map((last) => (last ? "   " : "│  "))
    .join("");
  return trunk + (row.isLast ? "└─ " : "├─ ");
}

/**
 * An em dash, not "0" — a process `ps` gave no reading for has not been
 * measured at zero, and the table must not imply that it has.
 */
export function formatCpuPercent(value: number | null | undefined): string {
  return typeof value === "number" ? `${value.toFixed(1)}%` : "—";
}

export function formatResidentMemory(rssKb: number | null | undefined): string {
  if (typeof rssKb !== "number") return "—";
  const mib = rssKb / 1024;
  if (mib >= 1024) return `${(mib / 1024).toFixed(2)} GiB`;
  if (mib >= 10) return `${Math.round(mib)} MiB`;
  return `${mib.toFixed(1)} MiB`;
}

export function statTone(stat: string): StatusTone {
  const state = stat[0]?.toUpperCase() ?? "";
  if (state === "R") return "ready";
  if (state === "Z") return "error";
  if (state === "T") return "warning";
  if (state === "I" || state === "S") return "muted";
  return "info";
}
